import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api/apiClient";
import type { CourierShift } from "../api/types";
import { buildMonthGrid, MonthCalendar } from "../components/MonthCalendar";
import { ReasonModal } from "../components/ui/ReasonModal";
import { formatTime24 } from "../utils/time24";

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function monthLabel(month: Date): string {
  return month.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

const STATUS_DOT: Record<CourierShift["status"], string> = {
  PLANNED: "is-planned",
  CONFIRMED: "is-confirmed",
  ENDED: "is-ended",
  MISSED: "is-missed",
  CANCELLED: "is-cancelled",
};

const STATUS_LABEL: Record<CourierShift["status"], string> = {
  PLANNED: "Planned",
  CONFIRMED: "Confirmed",
  ENDED: "Ended",
  MISSED: "Missed",
  CANCELLED: "Withdrawn",
};

type PendingAction =
  | { kind: "plan"; date: string }
  | { kind: "withdraw"; shift: CourierShift };

/**
 * G-2: the courier's own planning calendar (mobile-first — this page lives on
 * a phone). Plan days ahead, confirm today with one tap ("I'm available"),
 * end the day when done. Withdrawing an owner-planned day notifies the owner.
 */
export function CourierCalendarPage() {
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [shifts, setShifts] = useState<CourierShift[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<PendingAction | null>(null);

  const refresh = useCallback(async () => {
    const cells = buildMonthGrid(month);
    try {
      const list = await api.courierListSchedule(
        isoDate(cells[0].date),
        isoDate(cells[cells.length - 1].date),
      );
      setShifts(list);
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not load your calendar");
    }
  }, [month]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const byDate = useMemo(() => {
    const map = new Map<string, CourierShift>();
    for (const shift of shifts) {
      if (shift.status !== "CANCELLED") map.set(shift.date, shift);
    }
    return map;
  }, [shifts]);

  const todayIso = isoDate(new Date());
  const todayShift = byDate.get(todayIso) ?? null;

  const run = async (action: () => Promise<unknown>, failMessage: string) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      await refresh();
      setPending(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : failMessage);
    } finally {
      setBusy(false);
    }
  };

  const onDayTap = (date: Date) => {
    const iso = isoDate(date);
    if (iso < todayIso) return;
    const shift = byDate.get(iso);
    if (!shift) {
      setPending({ kind: "plan", date: iso });
    } else if (shift.status === "PLANNED" || shift.status === "CONFIRMED") {
      setPending({ kind: "withdraw", shift });
    }
  };

  return (
    <section className="page-stack courier-calendar">
      <div>
        <h2 className="page-title">My calendar</h2>
        <p className="page-subtitle">
          Tap a day to plan it, tap it again to withdraw. Confirm today when you
          start, end it when you finish.
        </p>
      </div>

      {error ? <div className="alert error">{error}</div> : null}

      <div className="card courier-today-card">
        {!todayShift ? (
          <>
            <p className="courier-today-state">You are not scheduled today.</p>
            <button
              type="button"
              className="button"
              disabled={busy}
              onClick={() =>
                void run(
                  () => api.courierPlanDay({ date: todayIso }),
                  "Could not plan today",
                )
              }
            >
              Work today
            </button>
          </>
        ) : todayShift.status === "PLANNED" ? (
          <>
            <p className="courier-today-state">
              Today is planned{todayShift.ownerCreated ? " by the shop" : ""}.
              Confirm when you start.
            </p>
            <button
              type="button"
              className="button"
              disabled={busy}
              onClick={() =>
                void run(
                  () => api.courierConfirmDay(todayShift.id),
                  "Could not confirm the day",
                )
              }
            >
              I&apos;m available
            </button>
          </>
        ) : todayShift.status === "CONFIRMED" ? (
          <>
            <p className="courier-today-state">
              On duty since{" "}
              {todayShift.confirmedAt ? formatTime24(todayShift.confirmedAt) : "—"}.
            </p>
            <button
              type="button"
              className="button"
              disabled={busy}
              onClick={() =>
                void run(
                  () => api.courierEndDay(todayShift.id),
                  "Could not end the day",
                )
              }
            >
              End my day
            </button>
          </>
        ) : (
          <p className="courier-today-state muted">
            {todayShift.status === "ENDED"
              ? `Day ended at ${todayShift.endedAt ? formatTime24(todayShift.endedAt) : "—"}.`
              : "Today's planned day was marked missed."}
          </p>
        )}
      </div>

      <div className="card">
        <div className="courier-month-nav">
          <button
            type="button"
            className="button plain"
            onClick={() =>
              setMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))
            }
          >
            ←
          </button>
          <strong>{monthLabel(month)}</strong>
          <button
            type="button"
            className="button plain"
            onClick={() =>
              setMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))
            }
          >
            →
          </button>
        </div>
        <MonthCalendar
          month={month}
          renderCell={(cell) => {
            const iso = isoDate(cell.date);
            const shift = byDate.get(iso);
            const tappable =
              iso >= todayIso &&
              (!shift ||
                shift.status === "PLANNED" ||
                shift.status === "CONFIRMED");
            return (
              <button
                type="button"
                className="courier-day"
                disabled={!tappable || busy}
                onClick={() => onDayTap(cell.date)}
                aria-label={`${iso}${shift ? ` — ${STATUS_LABEL[shift.status]}` : ""}`}
              >
                <span className="courier-day-num">{cell.date.getDate()}</span>
                {shift ? (
                  <span
                    className={`courier-day-dot ${STATUS_DOT[shift.status]}`}
                    aria-hidden="true"
                  />
                ) : null}
              </button>
            );
          }}
        />
        <div className="courier-legend muted">
          <span>
            <span className="courier-day-dot is-planned" /> Planned
          </span>
          <span>
            <span className="courier-day-dot is-confirmed" /> Confirmed
          </span>
          <span>
            <span className="courier-day-dot is-ended" /> Ended
          </span>
          <span>
            <span className="courier-day-dot is-missed" /> Missed
          </span>
        </div>
      </div>

      {pending?.kind === "plan" ? (
        <ReasonModal
          title="Plan this day"
          message={`Add ${pending.date} to your working calendar?`}
          confirmLabel={busy ? "Planning…" : "Plan day"}
          busy={busy}
          onConfirm={() =>
            void run(
              () => api.courierPlanDay({ date: pending.date }),
              "Could not plan the day",
            )
          }
          onClose={() => setPending(null)}
        />
      ) : null}

      {pending?.kind === "withdraw" ? (
        <ReasonModal
          title="Withdraw this day"
          message={
            pending.shift.ownerCreated
              ? `${pending.shift.date} was planned by the shop — withdrawing will notify the owner.`
              : `Remove ${pending.shift.date} from your calendar?`
          }
          reasonMode="optional"
          reasonLabel="Reason"
          reasonPlaceholder="e.g. bike repair"
          confirmLabel={busy ? "Withdrawing…" : "Withdraw day"}
          destructive
          busy={busy}
          onConfirm={(reason) =>
            void run(
              () => api.courierWithdrawDay(pending.shift.id, reason),
              "Could not withdraw the day",
            )
          }
          onClose={() => setPending(null)}
        />
      ) : null}
    </section>
  );
}
